import { useEffect, useState } from "react";
import storage, { setActiveEngagement, getActiveEngagement } from "./lib/storage.js";
import { supabase, isConfigured } from "./lib/supabase.js";
import Login from "./components/auth/Login.jsx";
import PendingApproval from "./components/auth/PendingApproval.jsx";
import EngagementsHome from "./components/EngagementsHome.jsx";
import RdStudyTool from "./components/RdStudyTool.jsx";

// `storage` import has the side effect of installing window.storage so
// RdStudyTool's existing useEffects can call window.storage.* unchanged.
void storage;

export default function App() {
  const [session, setSession] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileReady, setProfileReady] = useState(false);
  const [engagementId, setEngagementId] = useState(getActiveEngagement());
  const [externalUpdateNotice, setExternalUpdateNotice] = useState(false);

  // 1. Bootstrap auth session + subscribe
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session || null);
      setSessionReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (!s) {
        setProfile(null);
        setProfileReady(false);
        setEngagementId(null);
        setActiveEngagement(null);
      }
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // 2. Fetch profile when session lands (with one retry to wait for the
  //    handle_new_user trigger on first sign-in).
  useEffect(() => {
    if (!session) {
      setProfileReady(sessionReady);
      return;
    }
    let cancelled = false;
    const fetchProfile = async (attempt = 0) => {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("id, email, display_name, role, approved")
        .eq("id", session.user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.error("profile fetch error", error);
        setProfile(null);
        setProfileReady(true);
        return;
      }
      if (!data && attempt < 3) {
        // Trigger may not have fired yet; retry briefly
        setTimeout(() => fetchProfile(attempt + 1), 800);
        return;
      }
      setProfile(data || null);
      setProfileReady(true);
    };
    fetchProfile();
    return () => {
      cancelled = true;
    };
  }, [session, sessionReady]);

  // 3. Listen for real-time updates to the active engagement
  useEffect(() => {
    const handler = () => setExternalUpdateNotice(true);
    window.addEventListener("rd-engagement-external-update", handler);
    return () =>
      window.removeEventListener("rd-engagement-external-update", handler);
  }, []);

  const openEngagement = (id) => {
    setActiveEngagement(id);
    setEngagementId(id);
    setExternalUpdateNotice(false);
  };

  const goHome = () => {
    setActiveEngagement(null);
    setEngagementId(null);
    setExternalUpdateNotice(false);
  };

  // ----- render gates -----

  if (!isConfigured()) {
    return <ConfigError />;
  }

  if (!sessionReady) return <FullPageSpinner label="Loading…" />;

  if (!session) return <Login />;

  if (!profileReady) return <FullPageSpinner label="Loading your profile…" />;

  if (!profile) {
    return (
      <ConfigError
        title="Profile not found"
        message="We couldn't find a user_profiles row for your account. Try signing out and back in. If this persists, ask an admin to check the Supabase user_profiles table."
      />
    );
  }

  if (!profile.approved) return <PendingApproval profile={profile} />;

  if (!engagementId) {
    return <EngagementsHome profile={profile} onOpenEngagement={openEngagement} />;
  }

  return (
    <div className="relative">
      <RdStudyTool key={engagementId} />

      {/* Floating "Engagements" home button */}
      <button
        onClick={goHome}
        className="no-print fixed bottom-4 left-4 z-40 px-3 py-2 rounded-lg bg-stone-900 text-white text-xs sans shadow-lg hover:bg-stone-800 active:bg-stone-700 flex items-center gap-2"
        title="Back to engagements list"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        All studies
      </button>

      {/* External-update toast */}
      {externalUpdateNotice && (
        <div className="no-print fixed bottom-4 right-4 z-40 max-w-xs px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 shadow-lg sans">
          <div className="text-xs font-semibold text-amber-900 mb-1">
            Another user just edited this engagement
          </div>
          <div className="text-[11px] text-amber-800 mb-2">
            Reload to see their changes. Any unsaved edits in your view will
            be overwritten.
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => window.location.reload()}
              className="px-2.5 py-1 text-[11px] rounded bg-amber-700 text-white hover:bg-amber-800"
            >
              Reload
            </button>
            <button
              onClick={() => setExternalUpdateNotice(false)}
              className="px-2.5 py-1 text-[11px] rounded border border-amber-300 text-amber-800 hover:bg-amber-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FullPageSpinner({ label }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F7FA] sans">
      <div className="text-stone-500 text-sm">{label}</div>
    </div>
  );
}

function ConfigError({ title = "Configuration error", message }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F7FA] px-6 sans">
      <div className="max-w-md bg-white border border-rose-200 rounded-xl p-8">
        <div className="text-rose-700 text-sm font-semibold mb-2">{title}</div>
        <p className="text-stone-700 text-sm">
          {message ||
            "Supabase environment variables are missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in Cloudflare Pages → Settings → Environment variables (and in .env.local for local development), then redeploy."}
        </p>
      </div>
    </div>
  );
}
